// External
import React, { useMemo, useState, memo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import classNames from "classnames";
import { useQuery } from "@apollo/client";
//Internal
import { getFormattedUserName } from "Utils/GetFormattedUserName";
// Relative
import StudentListSkeleton from "../StudentListSkeleton";
import { getStudentYearGroup } from "../../modules/queries";
// Design System
import {
  Modal,
  Button,
  SearchInput,
  Alert,
  List,
  Checkbox,
  Avatar,
  EmptyState,
} from "@toddle-edu/ds-web";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
// Styles
import classes from "./CopyToOtherStudentsModal.scss";

const modalBodyStyles = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  paddingBottom: 0,
};

const CopyToOtherStudentsModal = props => {
  const {
    handleCloseModal,
    studentId,
    yearGroupId,
    yearGroupName,
    curriculumProgramsLabel,
    onApply,
    testId,
    globalGradeName,
  } = props;

  const { t } = useTranslation(["transcript", "common"]);

  const { data, networkStatus } = useQuery(getStudentYearGroup, {
    variables: {
      id: yearGroupId,
      filters: {
        orderBy: "FULL_NAME",
        orderByDirection: "ASC",
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !yearGroupId,
  });

  const isLoading = _.isEmpty(data) && _.includes([1, 2], networkStatus);

  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [searchText, setSearchText] = useState("");
  const normalizedSearchText = _.trim(searchText);
  const filteredStudents = useMemo(() => {
    const searchQuery = _.toLower(_.trim(searchText));

    return _.chain(data)
      .get("node.students.edges", [])
      .map("node")
      .filter(student => {
        if (!student) return false;
        if (studentId && student.id === studentId) return false;
        if (!_.isEmpty(searchQuery)) {
          const name = _.toLower(
            getFormattedUserName({ user: student, type: "student" })
          );
          return name.includes(searchQuery);
        }

        return true;
      })
      .value();
  }, [data, studentId, searchText]);

  const studentIdsSet = useMemo(
    () => new Set(filteredStudents.map(s => String(s.id))),
    [filteredStudents]
  );

  const selectedInFilteredCount = useMemo(() => {
    let count = 0;
    for (const id of selectedStudentIds) {
      if (studentIdsSet.has(String(id))) count++;
    }
    return count;
  }, [selectedStudentIds, studentIdsSet]);

  const isAllSelectedInFiltered =
    studentIdsSet.size > 0 && selectedInFilteredCount === studentIdsSet.size;

  const studentListData = useMemo(
    () =>
      filteredStudents.map(student => {
        const name = getFormattedUserName({
          user: student,
          type: "student",
        });

        return {
          id: student.id,
          value: name,
          subText: student.sourceId || "",
          prefix: (
            <Avatar
              src={student.profileImage}
              id={student.id}
              size="medium"
              name={name}
            />
          ),
          suffix: null,
        };
      }),
    [filteredStudents]
  );

  const handleSelectAll = useCallback(() => {
    if (studentIdsSet.size === 0) return;

    if (isAllSelectedInFiltered) {
      setSelectedStudentIds(prev => {
        const newSet = new Set(prev);
        for (const id of studentIdsSet) {
          newSet.delete(id);
        }
        return newSet;
      });
    } else {
      setSelectedStudentIds(prev => {
        const newSet = new Set(prev);
        for (const id of studentIdsSet) {
          newSet.add(id);
        }
        return newSet;
      });
    }
  }, [studentIdsSet, isAllSelectedInFiltered]);

  const handleRowSelection = useCallback(ids => {
    setSelectedStudentIds(new Set(ids));
  }, []);

  const handleApply = useCallback(async () => {
    if (selectedStudentIds.size === 0) return;
    if (onApply) {
      await onApply(Array.from(selectedStudentIds));
    }
  }, [selectedStudentIds, onApply]);

  const hasFilteredStudents = studentListData.length > 0;
  const showNoSearchResults =
    !hasFilteredStudents && !_.isEmpty(normalizedSearchText);
  const selectAllLabelClassName = classNames(
    classes.selectAllLabel,
    "text-body"
  );
  let listContent;

  if (isLoading) {
    listContent = <StudentListSkeleton />;
  } else if (showNoSearchResults) {
    listContent = (
      <EmptyState
        illustration={NoSearchResultsIllustration}
        title={t("common:noResultHeading")}
      />
    );
  } else {
    listContent = (
      <>
        {hasFilteredStudents && (
          <div className={classes.selectAllRow}>
            <div className={selectAllLabelClassName}>
              {t("common:select_all")}
            </div>
            <Checkbox
              testId={`${testId}-select-all`}
              label={t("common:select_all")}
              isChecked={isAllSelectedInFiltered}
              isIndeterminate={
                selectedInFilteredCount > 0 && !isAllSelectedInFiltered
              }
              onChange={handleSelectAll}
            />
          </div>
        )}
        <List
          data={studentListData}
          showSelection={true}
          selectionType="checkbox"
          selectItemPosition="right"
          rowsSelected={Array.from(selectedStudentIds)}
          onRowSelection={handleRowSelection}
          shouldStopCheckboxPropagation={true}
          testId={`${testId}-list`}
        />
      </>
    );
  }

  const searchPlaceholder = t("common:search_for_with_label", {
    label: t("common:student_plural"),
  });

  return (
    <Modal width={600} height={708} onClose={handleCloseModal}>
      <Modal.Header
        hasSeparator={true}
        title={t("transcript:copy_to_other_year_group_title", {
          yearGroupName,
        })}
        onClose={handleCloseModal}
        testId={`${testId}-header`}
      />
      <Modal.Body isScrollable={false} style={modalBodyStyles}>
        <div className={classes.container}>
          <Alert
            type="info"
            message={t("transcript:apply_to_multiple_students_alert_message", {
              globalGradeName,
              curriculumProgramsLabel,
            })}
          />

          <div className={classes.searchWrapper}>
            <div className={classes.searchRow}>
              <SearchInput
                testId={`${testId}-search`}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                size="medium"
                focusSearchBySlash={true}
                value={searchText}
                onChange={setSearchText}
              />
            </div>
            {isLoading ? null : (
              <div className="text-body">
                {t("common:student_count", { count: _.size(filteredStudents) })}
              </div>
            )}
          </div>
          <div className={classes.listContainer}>{listContent}</div>
        </div>
      </Modal.Body>
      <Modal.Footer hasSeparator={true}>
        <div className={classes.footer}>
          <div className={classes.footerLeft}>
            {!showNoSearchResults && (
              <div className="text-label">
                {t("transcript:count_student_selected", {
                  count: selectedStudentIds.size,
                })}
              </div>
            )}
          </div>
          <div className={classes.footerRight}>
            <Button
              variant="neutral"
              type="outlined"
              size="medium"
              testId={`${testId}-cancel`}
              onClick={handleCloseModal}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="progressive"
              size="medium"
              disabled={selectedStudentIds.size === 0}
              testId={`${testId}-apply`}
              onClick={handleApply}
            >
              {t("common:apply")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

CopyToOtherStudentsModal.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  studentId: PropTypes.string.isRequired,
  yearGroupId: PropTypes.string.isRequired,
  yearGroupName: PropTypes.string.isRequired,
  globalGradeName: PropTypes.string.isRequired,
  curriculumProgramsLabel: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

export default memo(CopyToOtherStudentsModal);
